import { Box, Card, CardContent, CardHeader, Typography } from '@mui/material';
import React, { ReactNode, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@cnx-saas/react-auth';
import { AllocationWidgets } from './AllocationWidgets';
import { LoadsWidgets } from './LoadsWidgets';
import { MasterDataCardHeader, MasterDataWidgets } from './MasterDataWidgets';
import { CurrentTransportsWidget } from './CurrentTransportsWidget';
import { CardLabel1 } from './CardParts';

type Widget = {
  id: string;
  headerTitle: string | React.ReactNode;
  content: ReactNode;
};

export const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth(true);

  const initialWidgets: Widget[] = [
    {
      id: 'loads',
      headerTitle: t('dashboard.header.loads'),
      content: <LoadsWidgets />,
    },
    {
      id: 'allocation',
      headerTitle: t('dashboard.header.allocation'),
      content: <AllocationWidgets />,
    },
    {
      id: 'transports',
      headerTitle: t('dashboard.header.transports'),
      content: <CurrentTransportsWidget />,
    },
    {
      id: 'MasterData',
      headerTitle: <MasterDataCardHeader />,
      content: <MasterDataWidgets />,
    },
    {
      id: 'analytics',
      headerTitle: t('dashboard.header.analytics'),
      content: <Typography variant="h6">No content</Typography>,
    },
  ];
  const getInitialWidgets = (): Widget[] => {
    const savedOrder = localStorage.getItem(`widgetsOrder_${user.tenantId}`);
    if (savedOrder) {
      const savedWidgetOrder = JSON.parse(savedOrder);
      return savedWidgetOrder
        .map((widgetId: string) =>
          initialWidgets.find((widget) => widget.id === widgetId),
        )
        .filter(Boolean);
    }
    return initialWidgets;
  };
  const [widgets, setWidgets] = useState(getInitialWidgets());

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWidgets(items);

    const widgetOrder = items.map((widget) => widget.id);
    localStorage.setItem(
      `widgetsOrder_${user.tenantId}`,
      JSON.stringify(widgetOrder),
    );
  };

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      p={4}
      pt={3}
      height={'95vh'}
      sx={(theme) => ({
        background: theme.palette.primary.light,
        overflow: 'auto',
      })}
    >
      <Typography variant="h4" align="left" gutterBottom mb={2} pl={1}>
        {t('dashboard.header.main')}
      </Typography>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="dashboard" direction="vertical">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              display="flex"
              flexDirection="column"
              gap={2}
            >
              {widgets.map((widget, index) => (
                <Draggable
                  key={widget.id}
                  draggableId={widget.id}
                  index={index}
                >
                  {(provided) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        position: 'relative',
                        borderRadius: '8px',
                      }}
                    >
                      <CardHeader
                        title={
                          <CardLabel1
                            label={widget.headerTitle}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        }
                        sx={{
                          backgroundColor: 'white',
                          padding: '8px 16px',
                        }}
                      />
                      <CardContent
                        sx={(theme) => ({
                          background: theme.palette.primary.lighter,
                          padding: '16px',
                        })}
                      >
                        {widget.content}
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};
